import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import TextField from '@mui/material/TextField';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';


import { FormControl } from '@mui/material';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { MapDataContext } from '../context/map-data';
import { DataSourceModel, MapLocation } from '../core/global';
import { extractLatLon, groupPointsByProximity } from '../helpers/common';
import { trackEvent } from '../helpers/tracking';
import styles from "../styles/components/simple-search.module.scss";


interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
  place_id: string;
}

interface SearchLocationProps {
  onLocationChange: (loc: MapLocation, model: DataSourceModel | null) => void
}

export default function SimpleSearch({
  onLocationChange,
}: SearchLocationProps) {
  const [locationValue, setLocationValue] = useState<MapLocation | null>(null);
  const [inputValue, setInputValue] = useState('');

  const {
    setMapCenter,
    dataSourceModel,
    setGridPoints,
    setActivePointId,
    setCurrentSelectionParams,
    activePointId,
  } = useContext(MapDataContext);

  const setMapLocation = useCallback(() => {
    if (locationValue) {
      onLocationChange(locationValue, dataSourceModel)

    } else {
      setMapCenter(null)
    }
  }, [locationValue, dataSourceModel])

  useEffect(() => {
    const extractedLocation = extractLatLon(inputValue)
    if (extractedLocation !== null) {
      setLocationValue(extractedLocation)
      trackEvent("location-search-location-match", {inputValue})
    } else {
      setLocationValue(null)
    }
  }, [inputValue]);


  const isOnDemand = useMemo(() => {
    return !!dataSourceModel?.key?.includes('ondemand');
  }, [dataSourceModel]);

  const label = useMemo(() => {
    if (isOnDemand) {
      return "Search lat, lon or paste from Excel"
    }
    return "Search lat, lon"
  }, [isOnDemand])

  const hasMultipleValues = useMemo(() => {
    return (isOnDemand && inputValue && typeof inputValue === 'string' && inputValue.indexOf("\t") !== -1)
  }, [inputValue, isOnDemand]);

  useEffect(() => {
    // // setActivePointId(id);
    let converted: (null | any)[] = inputValue.trim().split(/ /).map(i => {
      const loc = extractLatLon(i)
      if (loc) {
        const {location: {
          lat, lng
        }} = loc
        return [Number(lat), Number(lng)]
      }
      return null
    }).filter((val) => val !== null && val[0] !== null && val[1] !== null)

    if (isOnDemand && converted.length === 0) {
      const extractedLocation = extractLatLon(inputValue)
      if (extractedLocation !== null) {
        const {location: {
          lat, lng
        }} = extractedLocation
        converted = [[Number(lat), Number(lng)]]
      }
    }

    if (converted.length > 0) {
      const grouped = groupPointsByProximity(converted, 1)
    
      const nextGridPoints = grouped.map((group) => {
        const id = `grid-${nanoid()}`
        const centerPoints = group.points.map((point) => ({
          id: `point-${nanoid()}`,
          lat: point[0],
          lon: point[1],
        }))
    
        return {
          id,
          cenLat: group.centroid[0],
          cenLon: group.centroid[1],
          hidden: false,
          bounds: {
            centerLat: group.centroid[0],
            centerLon: group.centroid[1],
            centerPoints,
          },
        }
      })
    
      const activePointId = nextGridPoints[0]?.id ?? null
      const activeBounds = nextGridPoints[0]?.bounds
    
      setGridPoints(nextGridPoints)
      setActivePointId(activePointId)
    
      setCurrentSelectionParams((prev) => ({
        ...prev,
        activePointId,
        centerLat: activeBounds?.centerLat,
        centerLon: activeBounds?.centerLon,
        centerPoints: activeBounds?.centerPoints,
        gridPoints: nextGridPoints,
      }))
    }
    
  }, [inputValue])
  
  return (
    <div className={styles.searchInput}>
      <FormControl>
        <TextField
          inputProps={{ className: classNames(styles.searchInputInner, {
            [styles.largeInput]: isOnDemand
          }) }}
          size='small'
          id="search"
          name="search"
          label={label}
          placeholder={label}
          value={inputValue}
          onFocus={() => {
            setInputValue('')
          }}
          // autoFocus
          required
          fullWidth
          onKeyUp={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && (!hasMultipleValues || isOnDemand)) {
              setMapLocation()
            }
          }}
          // variant="outlined"
          onChange={(event: React.SyntheticEvent) => {
            // setOptions(newValue ? [newValue, ...options] : options);
            // @ts-ignore
            setInputValue(event.currentTarget.value)
          }}
          sx={{ ariaLabel: 'search' }}
        />
        <div className={styles.buttons}>
          <>
            <IconButton
              className={styles.clearButton}
              size="small"
              color="inherit"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault()
                setInputValue('')
              }}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              size="small"
              color="success"
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault()
                if (!hasMultipleValues || isOnDemand) {
                  setMapLocation()
                }
              }}
            >
              <CheckIcon />
            </IconButton>
          </>
        </div>
      </FormControl>
    </div>
  )
}
